import { apiList, apiCreate, apiUpdate, apiDelete, apiGet } from './apiClient';
export * from './intercomSettings';
import { apiOrigin } from '../utils/surface';

// Shadow-aware base. On shadow.app.intrlock.io routes hit the shadow APIGW;
// non-shadow builds retain the CES path. Prevents SignShop from silently
// 401-ing when a shadow-issued JWT is presented to CES.
const LAMBDA_BASE = apiOrigin();

export interface IntercomConfig {
  id: number;
  created_at: string;
  property_id: number | string;
  /** Phone number routing visitor calls into this intercom. */
  phone_number: string;
  /** Door devices this intercom can unlock — both video AND audio paths. */
  door_device_ids: (string | number)[];
  /** Kiosk activation code paired with the iPad/tablet kiosk app. */
  kiosk_code?: string;
  kiosk_code_expires_at?: string | null;
  last_activated_at?: string | null;
  /** Display label, e.g. "Lobby" or "South Gate". */
  label: string;
  enabled: boolean;
  /** Unit ids hidden from this intercom's visitor directory. PM-controlled,
   *  per-intercom override — useful when, e.g., a side-door intercom should
   *  only surface ground-floor units. Empty/undefined = show everyone. */
  hidden_unit_ids?: string[];
  /** Per-intercom PIN-pad override. `false` hides the PIN unlock button on
   *  THIS intercom only; `undefined` falls back to the property's
   *  pin_access_enabled setting. */
  pin_access_enabled?: boolean;
  /** Per-intercom Face ID override. Same fallback semantics. */
  face_access_enabled?: boolean;
  // ─── legacy fields (single-door rows from before the multi-door rework) ───
  device_id?: string | number;
  mode?: 'video' | 'audio';
}

const normalizeConfig = (c: any): IntercomConfig => {
  // Hydrate legacy single-door rows into the new multi-door shape so the UI
  // can treat every record uniformly.
  let doors: (string | number)[] = Array.isArray(c.door_device_ids)
    ? c.door_device_ids
    : typeof c.door_device_ids === 'string'
      ? (() => { try { return JSON.parse(c.door_device_ids); } catch { return []; } })()
      : [];
  if (doors.length === 0 && c.device_id) doors = [c.device_id];
  return { ...c, door_device_ids: doors };
};

export async function getIntercomConfigs(propertyId: number | string): Promise<IntercomConfig[]> {
  // Backend requires property_id — passing it as a query param scopes the
  // GSI lookup server-side. Without it the route returns 400 and the UI
  // silently shows an empty configuration list.
  const items = await apiList('intercom_configs', { property_id: String(propertyId) });
  return (items || []).map(normalizeConfig);
}

export async function regenerateKioskCode(id: number | string): Promise<{ kiosk_code: string; kiosk_code_expires_at?: string }> {
  const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
  const r = await fetch(`${LAMBDA_BASE}/intercom-configs/${id}/regenerate-kiosk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: '{}',
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error || `Failed to regenerate kiosk code (${r.status})`);
  return data;
}

export async function createIntercomConfig(
  data: Omit<IntercomConfig, 'id' | 'created_at'>
): Promise<IntercomConfig> {
  return apiCreate('intercom_configs', data);
}

export async function updateIntercomConfig(
  id: number,
  data: Partial<IntercomConfig>
): Promise<IntercomConfig> {
  return apiUpdate('intercom_configs', id, data);
}

export async function deleteIntercomConfig(id: number): Promise<void> {
  await apiDelete('intercom_configs', id);
}

// ── Tenant activation ──

export async function generateActivationCode(
  tenantId: number | string,
  propertyId: number | string
): Promise<{ activation_code: string; id: number }> {
  // Endpoint requires a JWT — pull the same token apiClient uses so
  // the call doesn't 401 silently and leave the UI stuck spinning.
  const token = localStorage.getItem('intrlock_token') || '';
  const r = await fetch(`${LAMBDA_BASE}/intercom/generate-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ tenant_id: tenantId, property_id: propertyId }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error || `Failed to generate code (${r.status})`);
  return data;
}

export async function getActivationForTenant(tenantId: number): Promise<any | null> {
  const items = await apiList('app_activations');
  return items.find((a: any) => a.tenant_id === tenantId) || null;
}

// ── Kiosk Activation ──

export async function generateKioskCode(
  propertyId: number
): Promise<{ code: string; id?: number }> {
  const r = await fetch(`${LAMBDA_BASE}/video/generate-kiosk-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ property_id: propertyId }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Failed to generate kiosk code');
  return { code: data.code || data.activation_code, id: data.id };
}

export async function sendKioskCode(
  propertyId: number,
  email: string,
  code?: string
): Promise<{ success: boolean; code?: string }> {
  const r = await fetch(`${LAMBDA_BASE}/video/send-kiosk-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ property_id: propertyId, email, code }),
  });
  const data = await r.json();
  return { success: data.success || r.ok, code: data.code };
}

export async function getKioskActivations(propertyId: number): Promise<any[]> {
  const items = await apiList('app_activations');
  return items.filter(
    (a: any) =>
      a.property_id === propertyId &&
      (a.app_type === 'kiosk' ||
        a.device_type === 'kiosk' ||
        (a.platform || '').toLowerCase() === 'ipados')
  );
}
